import { getCookie } from './cookieHelpers'

//product urls will use domain cookie for EMEA for now
export function currentLocale() {
  return getCookie("insight_locale");
}

/**
 * Builds a URL for the product details page of a passed in item. Mostly just
 * concatenates some properties.
 * Based on `CommonUtil.productdetailURLMap`
 * @param  {Object} {
 *         locale
 *         materialId
 *         mfrName
 *         mfrId
 *         description
 * }                                        Required product information to build the URL
 * @return {String}                         A URL to the product details page of the passed in item.
 */
export function makeProductDetailURL({ locale = currentLocale(), materialId = required(),
                                       mfrName = required(), mfrId = required(), description = required() }) {
  description = description
    .replace(/[\.,-\/#!$%\"\'\^&\*;:{}=\-_`~()]/g, '')
    .trim()
    .replace(/ +(?= )/g, '')
    .replace(/\s/g, '-')

  return `/${locale}/shop/product/${encodeURIComponent(materialId)}/${encodeURIComponent(mfrName)}/${encodeURIComponent(mfrId)}/${description}/`
}

/**
 * Simply throws an error on invokation. Useful for marking parameters as required.
 * Could do some currying for more descriptive Errors. Would be good.
 * @return {Error}
 */
function required() {
  throw Error('Function missing necessary argument')
}

export function triggerTrackingUrl(trackingUrl, onLoad, onError) {
  if (trackingUrl) {
    var img = new Image();
    // Not to block the user flow, keeping a fallback of onLoad function for onerror.
    if (onLoad) {
      img.onload = onLoad;
      img.onerror = onError || onLoad;
    }
    img.src = trackingUrl;
  }
}

export function productImageToRender(image){
  function isValidImage(value) {
    return !!value && value !== 'image.not.available'
  }
  if(!image) {
    return '/content/dam/insight-web/source/img/noImageAvailable_150x112.png'
  } else if(isValidImage(image.extraLargeImage)){
    return image.extraLargeImage;
  } else if(isValidImage(image.largeImage)){
    return image.largeImage;
  } else if(isValidImage(image.smallImage)){
    return image.smallImage;
  } else if(isValidImage(image.manufacturerExtraLargeImage)){
    return image.manufacturerExtraLargeImage;
  } else if(isValidImage(image.manufacturerLargeImage)){
    return image.manufacturerLargeImage;
  } else if(isValidImage(image.manufacturerSmallImage)){
    return image.manufacturerSmallImage;
  }else{
    return '/content/dam/insight-web/source/img/noImageAvailable_150x112.png'
  }
};

/**
 *  From an array of @param prices, choose the pricing option to use.
 *
 * If the user is browsing under a particular contract and that contract has special
 * pricing, (as generally indicated by the response of some request to get product
 * information, such as `compareProducts`) use the contract's pricing. This already
 * accounts for finding the cheapest of various prices.
 *
 * If the user is browsing freely they may see open market or standard product
 * listing prices.
 *
 * Some products require special pricing and the user must call to receive a quote.
 *
 * NOTE: Generally: Contract prices > Call for quote > Your Price > Open Market Price > List Price
 *
 * NOTE: Logic abstracted from the definition of getPriceFromWebProduct in productinfo.js
 *
 * @param  {Array<Object>} prices           Collection of price objects, usually of shape:
 *                                          { currency, inventoryPrice, morePricesAvailable, price, priceLabel }
 * @param  {Object} [contractDisplayInfo={}] If a user is browsing under a specific contract. If
 *                                           this item has special pricing, it will be held here.
 * @return {Object}                          The price Object from the collection with the correct
 *                                           pricing information
 */
export function getAppropriateProductPrice(prices, contractDisplayInfo = null) {
  let appropriatePrice
  const priceMap = priceTypes(prices)

  if (contractDisplayInfo != null) {
    return {
      price: contractDisplayInfo.contractPrice,
      currency: contractDisplayInfo.currency || 'USD',
      contractTitle: contractDisplayInfo.contractShortTitle || contractDisplayInfo.contractTitle || '',
    }
  } else if (priceMap.isInventoryPrice) {
    appropriatePrice = prices.find(price => ['COIPRICELABEL', 'CSIPRICELABEL'].includes(price.priceLabel))
  } else if (priceMap.callForPrice) {
    appropriatePrice = prices.find(price => price.priceLabel === 'CALLFORPRICELABEL')
  } else if (priceMap.yourPrice != null) {
    appropriatePrice = prices.find(price => price.priceLabel === 'YOURPRICELABEL')
  } else if (priceMap.openMarketPrice != null) {
    appropriatePrice = prices.find(price => price.priceLabel === 'OPENMARKETPRICELABEL')
  } else {
    appropriatePrice = prices.find(price => price.priceLabel === 'LISTPRICELABEL' || price.priceLabel === null)
  }

  return appropriatePrice
}

/**
 * Turn an array of price objects into an object with keys representing the available prices.
 * This seems clearly redundant / unnecessary. The parsing is weird so I haven't yet figured out
 * the better alternative. This is taken from `function getPrices(prices)` of productinfo.js
 * @param  {Array<Object>} prices       Collection of price objects, usually of shape:
 *                                      { currency, inventoryPrice, morePricesAvailable, price, priceLabel }
 * @return {Object}                     generally of shape {listPrice?, openMarketPrice?, yourPrice?,
 *                                                callForPrice?, isInventoryPrice?}
 */
function priceTypes(prices) {
  return prices.reduce((acc, priceGroup) => {
    const { price, inventoryPrice } = priceGroup
    let { priceLabel } = priceGroup

    if (priceLabel == null) priceLabel = 'NULL'
    switch (priceLabel) {
      case 'LISTPRICELABEL':
        acc.listPrice = price
        break
      case 'OPENMARKETPRICELABEL':
        acc.openMarketPrice = price
        break
      case 'YOURPRICELABEL':
        acc.yourPrice = price
        break
      case 'COIPRICELABEL':
      case 'CSIPRICELABEL':
        acc.yourPrice = inventoryPrice
        acc.isInventoryPrice = true
        break
      case 'CALLFORPRICELABEL':
        acc.yourPrice = price
        acc.callForPrice = priceLabel
        break
      case 'NULL':
        if (acc.listPrice != null) acc.listPrice = price
        break
      case 'LEASEPRICELABEL':
        acc.leasePrice = price
        break
      case 'VATPRICELABEL':
        acc.vatPrice = price
        break
      default: throw Error(`Error parsing price: ${JSON.stringify(priceGroup)}`)
    }

    return acc
  }, {})
}

